/**
 * Auth store — JWT + refresh token in localStorage, user profile in memory.
 *
 * Storage rationale:
 * - localStorage (not sessionStorage) so the session survives PWA closures
 *   and cold launches.
 *
 * Hydration:
 * - Stores are client-only. We hydrate from localStorage on first
 *   mount via `hydrateFromStorage()` called from <AuthProvider>.
 *
 * The store registers itself with `lib/api` via `setAuthHelpers` so the API
 * client can read the token and trigger refresh on 401 without importing
 * the store directly (avoids a circular import).
 */

import { writable, get } from 'svelte/store';
import { api, ApiError, setAuthHelpers } from '@/lib/api';
import type { AuthResponse, UserPublic } from '@/lib/types/auth';

const TOKEN_KEY = 'tickedge.jwt';
const REFRESH_KEY = 'tickedge.refresh';
const USER_KEY = 'tickedge.user';

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: UserPublic | null;
  hydrated: boolean;
}

const INITIAL: AuthState = {
  token: null,
  refreshToken: null,
  user: null,
  hydrated: false
};

function readStorage(): { token: string | null; refresh: string | null; user: UserPublic | null } {
  if (typeof window === 'undefined') {
    return { token: null, refresh: null, user: null };
  }
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const refresh = localStorage.getItem(REFRESH_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    return {
      token,
      refresh,
      user: userJson ? (JSON.parse(userJson) as UserPublic) : null
    };
  } catch {
    return { token: null, refresh: null, user: null };
  }
}

function writeStorage(token: string | null, refresh: string | null, user: UserPublic | null) {
  if (typeof window === 'undefined') return;
  // One-time migration: if any legacy sessionStorage values exist from
  // an earlier build that used sessionStorage, clear them so a future
  // re-introduction of sessionStorage can't shadow the localStorage entries.
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(USER_KEY);
  } catch {
    // Ignore — sessionStorage may be unavailable in some embedded contexts.
  }
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  else localStorage.removeItem(REFRESH_KEY);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

/**
 * Module-level in-flight refresh, shared across ALL callers of `refreshSession()`.
 *
 * Without this, concurrent 401s (e.g., TanStack Query refetchOnWindowFocus
 * firing three queries in parallel after the app comes back to foreground)
 * each trigger their own POST /v1/auth/refresh. If the backend rotates
 * refresh tokens, the second and third requests fail with 401 and drop
 * into the catch block — logging the user out even though refresh #1
 * succeeded.
 */
let refreshPromise: Promise<string | null> | null = null;

const _state = writable<AuthState>(INITIAL);

export const authStore = {
  subscribe: _state.subscribe
};

export function hydrateFromStorage(): void {
  const { token, refresh, user } = readStorage();
  _state.set({ token, refreshToken: refresh, user, hydrated: true });
}

export function setSession(resp: AuthResponse): void {
  writeStorage(resp.token, resp.refresh_token, resp.user);
  _state.set({
    token: resp.token,
    refreshToken: resp.refresh_token,
    user: resp.user,
    hydrated: true
  });
}

export function clearSession(): void {
  writeStorage(null, null, null);
  _state.update((s) => ({ ...s, token: null, refreshToken: null, user: null }));
}

export async function refreshSession(): Promise<string | null> {
  const current = get(_state).refreshToken;
  if (!current) return null;

  // Coalesce concurrent refreshes — see `refreshPromise` doc above.
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const resp = await api<AuthResponse>('/v1/auth/refresh', {
        method: 'POST',
        anonymous: true,
        body: { refresh_token: current }
      });
      writeStorage(resp.token, resp.refresh_token, resp.user);
      _state.update((s) => ({
        ...s,
        token: resp.token,
        refreshToken: resp.refresh_token,
        user: resp.user
      }));
      return resp.token;
    } catch (err) {
      // Only clear the session when the SERVER actually rejects the
      // refresh token. Network failures (offline, DNS, ALB 5xx, CORS
      // preflight blip, AbortError) must NOT log the user out — the
      // refresh token is still perfectly valid, the user just briefly
      // lost connectivity.
      //
      // 401 = expired/invalid refresh token (real auth failure)
      // 403 = refresh token revoked (real auth failure)
      // anything else = transient — keep the session and let the caller retry.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        writeStorage(null, null, null);
        _state.update((s) => ({ ...s, token: null, refreshToken: null, user: null }));
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** Synchronous read of the current auth state (svelte/store get). */
export function getAuthState(): AuthState {
  return get(_state);
}

// Wire the API client to the store. This runs once at module-evaluation time
// on the client, so any module that imports the store also wires the helpers.
setAuthHelpers(
  () => get(_state).token,
  () => refreshSession()
);
